import { yupResolver } from "@hookform/resolvers/yup";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import CheckIcon from "@mui/icons-material/Check";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";

import Copyright from "../../../components/Copyright";
import ControlledCheckbox from "../../../components/controlled/ControlledCheckbox";
import ControlledPhoneInput from "../../../components/controlled/ControlledPhoneInput";
import ControlledTextField from "../../../components/controlled/ControlledTextField";

import backgroundImage from "../../../assets/background.jpg";
import logoImage from "../../../assets/logos/logo.png";

import { useRegisterMutation } from "../authApiSlice";

import useBackgroundWithSVGPlaceholder from "../../../hooks/useBackgroundWithSVGPlaceholder";
import useRecaptchaEnterprise from "../../../hooks/useRecaptchaEnterprise";

import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { getFormSchema } from "./formSchema";

export default function SignInSide() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { enqueueSnackbar } = useSnackbar();
	const [register, { isLoading }] = useRegisterMutation();
	const { getToken: getRecaptchaToken } = useRecaptchaEnterprise();

	const formSchema = getFormSchema(t);
	const {
		handleSubmit,
		control,
		formState: { errors },
		setError,
	} = useForm({
		resolver: yupResolver(formSchema),
		shouldFocusError: true,
	});
	const backgroundImg = useBackgroundWithSVGPlaceholder(backgroundImage);

	const onSubmit = async ({
		companyName,
		nip,
		firstName,
		lastName,
		email,
		phoneNumber,
		password,
		passwordConfirm,
		rulesAgreement,
		dataProcessingAgreement,
	}) => {
		let recaptchaToken;
		try {
			recaptchaToken = await getRecaptchaToken("register");
		} catch {
			enqueueSnackbar(t("default_error"), {
				variant: "error",
				preventDuplicate: true,
			});
			return;
		}

		await register({
			companyName,
			nip,
			firstName,
			lastName,
			email,
			phoneNumber: phoneNumber.replaceAll(" ", ""),
			password,
			password_confirmation: passwordConfirm,
			rulesAgreement,
			dataProcessingAgreement,
			recaptchaToken,
		})
			.unwrap()
			.then((_payload) => {
				enqueueSnackbar(t("registered_company_await_email_confirmation"));
				setTimeout(() => {
					navigate("/thank-you?source=registration");
				}, 500);
			})
			.catch((error) => {
				if (error?.data?.errors) {
					const errors = error.data.errors;
					Object.entries(errors).forEach(([key, message]) => {
						setError(key);
						enqueueSnackbar(message[0], {
							variant: "error",
							preventDuplicate: true,
						});
					});
				} else {
					enqueueSnackbar(error?.data?.message || t("default_error"), {
						variant: "error",
						preventDuplicate: true,
					});
				}
			});
	};

	useEffect(() => {
		if (Object.keys(errors).length > 0) {
			Object.values(errors).forEach((err) => {
				if (err.message) {
					enqueueSnackbar(err.message, {
						variant: "error",
						preventDuplicate: true,
					});
				}
			});
		}
	}, [errors, enqueueSnackbar]);

	return (
		<Grid container component="main" sx={{ height: "100vh" }}>
			<CssBaseline />
			<Grid
				sx={{
					backgroundColor: "#084170",
					backgroundImage: backgroundImg,
					backgroundRepeat: "no-repeat",
					backgroundSize: "cover",
					backgroundPosition: "74%",
				}}
				size={{
					xs: false,
					sm: 4,
					md: 5,
				}}
			>
				<Box
					sx={{
						p: 4,
						px: 8,
						height: "100%",
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					<Box sx={{ mb: 4, display: "flex", justifyContent: "center" }}>
						<img
							src={logoImage}
							alt="CargoSeller"
							width="268"
							style={{ maxWidth: "100%" }}
						/>
					</Box>
					<Box>
						<Box sx={{ px: 1, mb: 1 }}>
							<Typography component="h2" variant="h6" color="#fff">
								{t("enter_cargo_and_allow_cargoseller_automation_work")}
							</Typography>
						</Box>
						<List dense sx={{ color: "#fff" }}>
							<ListItem>
								<ListItemIcon>
									<CheckIcon sx={{ color: "#fff" }} />
								</ListItemIcon>
								<ListItemText
									disableTypography
									sx={{ fontSize: "18px" }}
									primary={t("we_send_cargo_to_3_stocks")}
								/>
							</ListItem>
							<ListItem>
								<ListItemIcon>
									<CheckIcon sx={{ color: "#fff" }} />
								</ListItemIcon>
								<ListItemText
									disableTypography
									sx={{ fontSize: "18px" }}
									primary={t("we_remove_cargo_from_3_stocks")}
								/>
							</ListItem>
							<ListItem>
								<ListItemIcon>
									<CheckIcon sx={{ color: "#fff" }} />
								</ListItemIcon>
								<ListItemText
									disableTypography
									sx={{ fontSize: "18px" }}
									primary={t("we_send_mailing_to_defined_groups")}
								/>
							</ListItem>
							<ListItem>
								<ListItemIcon>
									<CheckIcon sx={{ color: "#fff" }} />
								</ListItemIcon>
								<ListItemText
									disableTypography
									sx={{ fontSize: "18px" }}
									primary={t("we_search_for_free_vehicles_timocom")}
								/>
							</ListItem>
							<ListItem>
								<ListItemIcon>
									<CheckIcon sx={{ color: "#fff" }} />
								</ListItemIcon>
								<ListItemText
									disableTypography
									sx={{ fontSize: "18px" }}
									primary={t("we_auto_refresh_offer_on_stocks")}
								/>
							</ListItem>
							<ListItem>
								<ListItemIcon>
									<CheckIcon sx={{ color: "#fff" }} />
								</ListItemIcon>
								<ListItemText
									disableTypography
									sx={{ fontSize: "18px" }}
									primary={t("we_gather_propositions_from_stocks_and_mailing")}
								/>
							</ListItem>
							<ListItem>
								<ListItemIcon>
									<CheckIcon sx={{ color: "#fff" }} />
								</ListItemIcon>
								<ListItemText
									disableTypography
									sx={{ fontSize: "18px" }}
									primary={t("we_provide_sales_statistics")}
								/>
							</ListItem>
						</List>
					</Box>
				</Box>
			</Grid>
			<Grid
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
				}}
				size={{
					xs: 12,
					sm: 8,
					md: 7,
				}}
			>
				<Box
					sx={{
						my: 8,
						mx: 4,
						width: "60%",
					}}
				>
					<Typography component="h1" variant="h5">
						{t("new_company_registration")}
					</Typography>
					<Box
						component="form"
						noValidate
						onSubmit={handleSubmit(onSubmit)}
						sx={{ mt: 1 }}
					>
						<Grid container spacing={2}>
							<Grid
								size={{
									xs: 12,
									sm: 8,
									md: 8,
								}}
							>
								<ControlledTextField
									shrinkLabel={false}
									control={control}
									margin="normal"
									name="companyName"
									label={t("company_name_label")}
									required
									error={!!errors?.companyName}
									fullWidth
									autoFocus
								/>
							</Grid>
							<Grid
								size={{
									xs: 12,
									sm: 4,
									md: 4,
								}}
							>
								<ControlledTextField
									shrinkLabel={false}
									control={control}
									margin="normal"
									name="nip"
									label={t("nip_label")}
									required
									error={!!errors?.nip}
									fullWidth
								/>
							</Grid>
						</Grid>
						<Grid container spacing={2}>
							<Grid
								size={{
									xs: 12,
									sm: 6,
									md: 6,
								}}
							>
								<ControlledTextField
									shrinkLabel={false}
									control={control}
									margin="normal"
									name="firstName"
									label={t("first_name_label")}
									required
									error={!!errors?.firstName}
									fullWidth
								/>
							</Grid>
							<Grid
								size={{
									xs: 12,
									sm: 6,
									md: 6,
								}}
							>
								<ControlledTextField
									shrinkLabel={false}
									control={control}
									margin="normal"
									name="lastName"
									label={t("surname_label")}
									required
									error={!!errors?.lastName}
									fullWidth
								/>
							</Grid>
						</Grid>
						<Grid container spacing={2}>
							<Grid
								size={{
									xs: 12,
									sm: 6,
									md: 6,
								}}
							>
								<ControlledTextField
									shrinkLabel={false}
									control={control}
									margin="normal"
									name="email"
									label={t("email_label")}
									type="email"
									autoComplete="email"
									required
									error={!!errors?.email}
									fullWidth
								/>
							</Grid>
							<Grid
								size={{
									xs: 12,
									sm: 6,
									md: 6,
								}}
							>
								<ControlledPhoneInput
									shrinkLabel={false}
									control={control}
									margin="normal"
									name="phoneNumber"
									label={t("phone_number_label")}
									type="tel"
									autoComplete="tel"
									required
									error={!!errors?.phoneNumber}
									defaultValue="+48"
								/>
							</Grid>
						</Grid>
						<Grid container spacing={2}>
							<Grid
								size={{
									xs: 12,
									sm: 6,
									md: 6,
								}}
							>
								<ControlledTextField
									shrinkLabel={false}
									sx={{ background: "#fff" }}
									control={control}
									margin="normal"
									name="password"
									label={t("password_label")}
									type="password"
									required
									error={!!errors?.password}
									fullWidth
								/>
							</Grid>
							<Grid
								size={{
									xs: 12,
									sm: 6,
									md: 6,
								}}
							>
								<ControlledTextField
									shrinkLabel={false}
									sx={{ background: "#fff" }}
									control={control}
									margin="normal"
									name="passwordConfirm"
									label={t("confirm_password_label")}
									type="password"
									required
									error={!!errors?.passwordConfirm}
									fullWidth
								/>
							</Grid>
						</Grid>
						<Box sx={{ mt: 2 }}>
							{/* todo: translate URLs */}
							<ControlledCheckbox
								control={control}
								margin="normal"
								name="rulesAgreement"
								label={
									<span>
										{t("i_accept")}{" "}
										<a
											href="https://cargoseller.pl/regulamin/"
											target="_blank"
											rel="noopener noreferrer"
										>
											{t("terms_of_service")}
										</a>{" "}
										{t("and_1")}{" "}
										<a
											href="https://cargoseller.pl/polityka-prywatnosci/"
											target="_blank"
											rel="noopener noreferrer"
										>
											{t("privacy_policy")}
										</a>
									</span>
								}
								error={!!errors?.rulesAgreement}
							/>
							<ControlledCheckbox
								control={control}
								margin="normal"
								name="dataProcessingAgreement"
								label={t("i_consent_to_process_my_personal_data")}
								error={!!errors?.dataProcessingAgreement}
							/>
						</Box>
						<Box sx={{ display: "flex", justifyContent: "center" }}>
							<Button
								loading={isLoading}
								type="submit"
								variant="contained"
								sx={{ mt: 3, mb: 2 }}
							>
								{t("create_account")}
							</Button>
						</Box>
						<Copyright sx={{ mt: 5, color: "#000" }} />
					</Box>
				</Box>
			</Grid>
		</Grid>
	);
}
